import { grey, blue, purple } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import fi from 'date-fns/locale/fi'
import { Chart, ChartData, ChartDataset, ChartOptions, ScatterDataPoint } from 'chart.js'
import 'chartjs-adapter-date-fns'
import zoom from 'chartjs-plugin-zoom'
import {
  getDifferenceOfDateRangeInHours,
  humanizeDateRange,
  humanizeTimestamp
} from '../utils/date'
import LoadingState from './LoadingState'
import _ from 'lodash'
import { useTheme } from '@mui/material'
import {
  DateRange,
  RuuviTagAcceleration,
  RuuviTagAccelerationField,
  RuuviTagAccelerationQuery,
  RuuviTagAccelerationStatistic
} from '../types'
import { useDispatch } from 'react-redux'
import { setDateRange, setTimePeriod } from '../redux/slices/query'
import TimePeriod from '../enums/TimePeriod'
import LineChart from './LineChart'

Chart.register(zoom)

export interface RuuviTagAccelerationChart {
  ruuviTagAccelerations: RuuviTagAcceleration[]
  ruuviTagAccelerationQuery: RuuviTagAccelerationQuery
  ruuviTagAccelerationFields: RuuviTagAccelerationField[]
  ruuviTagAccelerationStatistics: RuuviTagAccelerationStatistic[]
  /**
   * The date range.
   */
  dateRange?: DateRange

  chartOptions?: ChartOptions
  chartWrapperHeight?: string
  loading?: boolean
}

export default function RuuviTagAccelerationChart({
  ruuviTagAccelerations,
  ruuviTagAccelerationQuery,
  ruuviTagAccelerationFields,
  ruuviTagAccelerationStatistics,
  dateRange,
  chartOptions,
  chartWrapperHeight = '320px',
  loading = false
}: RuuviTagAccelerationChart) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The difference of date range as hours.
   */
  const differenceOfDateRangeInHours = getDifferenceOfDateRangeInHours(
    dateRange ? dateRange.from : ruuviTagAccelerationQuery.from,
    dateRange ? dateRange.to : ruuviTagAccelerationQuery.to
  )

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Update the date range.
   */
  function updateDateRange({ chart }: { chart: Chart }) {
    const { min, max } = chart.scales.x

    dispatch(setTimePeriod(TimePeriod.CUSTOM))
    dispatch(
      setDateRange({
        from: new Date(parseInt(min.toString())),
        to: new Date(parseInt(max.toString()))
      })
    )
  }

  const chartData: ChartData = {
    datasets: makeChartDatasets()
  }

  const defaultChartOptions: ChartOptions<'line'> = {
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true
          },
          mode: 'x',
          onZoomComplete: updateDateRange
        }
      },
      title: {
        display: true,
        position: 'bottom',
        padding: {
          top: 24
        },
        text: `${humanizeDateRange(
          dateRange ? dateRange.from : ruuviTagAccelerationQuery.from,
          dateRange ? dateRange.to : ruuviTagAccelerationQuery.to
        )}`,
        font: {
          // @ts-expect-error typed incorrectly
          weight: '600',
          size: 14
        },
        color: theme.palette.text.primary
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 24,
          boxHeight: 1,
          padding: 24,
          font: {
            // @ts-expect-error typed incorrectly
            weight: '600',
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems): string => {
            if (tooltipItems.length) {
              // @ts-expect-error typed incorrectly
              return humanizeTimestamp(tooltipItems[0].raw.x, true)
            }

            return ''
          }
        }
      },
      decimation: {
        enabled: false,
        algorithm: 'min-max'
      }
    },
    elements: {
      point: {
        pointStyle: 'point',
        radius: 0
      },
      line: {
        borderWidth: 1
      }
    },
    scales: {
      x: {
        type: 'time',
        display: true,
        // @ts-expect-error typed incorrectly
        min: dateRange ? dateRange.from : ruuviTagAccelerationQuery.from,
        // @ts-expect-error typed incorrectly
        max: dateRange ? dateRange.to : ruuviTagAccelerationQuery.to,
        time: {
          unit:
            differenceOfDateRangeInHours <= 3
              ? 'minute'
              : differenceOfDateRangeInHours <= 24
                ? 'hour'
                : 'day',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'd.M'
          }
        },
        adapters: {
          date: {
            locale: fi
          }
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          source: 'auto'
        },
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        },
        color: '#fff'
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        }
      }
    }
  }

  chartOptions = _.merge(defaultChartOptions, chartOptions)

  function makeChartDatasets(): ChartDataset[] {
    const datasets: ChartDataset[] = []

    for (const field of ruuviTagAccelerationFields) {
      for (const statistic of ruuviTagAccelerationStatistics) {
        const label = makeDatasetLabel(field, statistic)
        const color = makeDatasetColor(field, statistic)
        const data = makeDatasetData(field, statistic)

        datasets.push({
          label,
          backgroundColor: color,
          borderColor: color,
          tension: 0.4,
          spanGaps: false,
          data: data as unknown as ScatterDataPoint[]
        })
      }
    }

    return datasets
  }

  /**
   * Make the key.
   */
  function makeKey(
    field: RuuviTagAccelerationField,
    statistic: RuuviTagAccelerationStatistic
  ): keyof RuuviTagAcceleration {
    return _.camelCase(`${field}_${statistic}`) as keyof RuuviTagAcceleration
  }

  /**
   * Make the label for the dataset.
   */
  function makeDatasetLabel(
    field: RuuviTagAccelerationField,
    statistic: RuuviTagAccelerationStatistic
  ): string {
    return `${field.toUpperCase()} (${t(statistic)})`
  }

  /**
   * Make the color for the dataset.
   */
  function makeDatasetColor(
    field: RuuviTagAccelerationField,
    statistic: RuuviTagAccelerationStatistic
  ) {
    let weight: 'A700' | '500'
    switch (statistic) {
      case 'max':
        weight = 'A700'
        break
      case 'avg':
      default:
        weight = '500'
        break
    }

    // @see https://mui.com/material-ui/customization/color/#color-palette
    switch (field) {
      case 'x':
        return grey[weight]
      case 'y':
        return purple[weight]
      case 'z':
      default:
        return blue[weight]
    }
  }

  /**
   * Make the data for the dataset.
   */
  function makeDatasetData(
    field: RuuviTagAccelerationField,
    statistic: RuuviTagAccelerationStatistic
  ) {
    const key = makeKey(field, statistic)

    return ruuviTagAccelerations.map((ruuviTagAcceleration) => {
      return {
        x: ruuviTagAcceleration.time,
        y: ruuviTagAcceleration[key]
      }
    })
  }

  return (
    <>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <LineChart data={chartData} options={chartOptions} wrapperHeight={chartWrapperHeight} />
        </>
      )}
    </>
  )
}
